import { useState } from 'react';
import { ArrowLeft, Shield, FileText, AlertCircle } from 'lucide-react';
import { theme } from '../lib/theme';

const EFFECTIVE_DATE = 'April 21, 2026';
const COMPANY_NAME = 'Trovva Inc.';
const PRODUCT_NAME = 'Trovva';
const SUPPORT_EMAIL = 'support@trovva.ai';
const LEGAL_EMAIL = 'legal@trovva.ai';
const WEBSITE = 'https://trovva.ai';

export default function TermsOfService({ onBack }) {
  const [activeSection, setActiveSection] = useState(null);

  const sectionStyle = {
    marginBottom: 32,
  };
  const headingStyle = {
    fontSize: 18, fontWeight: 700, color: theme.colors.text,
    marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
  };
  const subHeadingStyle = {
    fontSize: 15, fontWeight: 600, color: theme.colors.text,
    marginBottom: 8, marginTop: 16,
  };
  const paraStyle = {
    fontSize: 14, lineHeight: 1.7, color: theme.colors.textLight,
    marginBottom: 12,
  };
  const listStyle = {
    fontSize: 14, lineHeight: 1.8, color: theme.colors.textLight,
    margin: '8px 0 12px 20px', paddingLeft: 0,
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.bg,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: '16px 24px',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          {onBack && (
            <button onClick={onBack} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              color: theme.colors.primary, fontSize: 14, fontWeight: 500,
            }}>
              <ArrowLeft size={18} /> Back
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryLight})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, color: 'white', fontSize: 16,
            }}>T</div>
            <span style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text }}>Trovva</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px 80px' }}>
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <FileText size={28} color={theme.colors.primary} />
            <h1 style={{ fontSize: 28, fontWeight: 800, color: theme.colors.text, margin: 0 }}>
              Terms of Service
            </h1>
          </div>
          <p style={{ fontSize: 14, color: theme.colors.textLight, margin: 0 }}>
            Effective Date: {EFFECTIVE_DATE} &nbsp;|&nbsp; Last Updated: {EFFECTIVE_DATE}
          </p>
        </div>

        {/* Intro */}
        <div style={sectionStyle}>
          <p style={paraStyle}>
            Welcome to {PRODUCT_NAME}. These Terms of Service ("Terms") constitute a legally binding
            agreement between you ("you," "your," or "User") and {COMPANY_NAME}, a New Jersey corporation
            ("{PRODUCT_NAME}," "we," "us," or "our"), governing your access to and use of the {PRODUCT_NAME} platform,
            including all software, services, APIs, and related features (collectively, the "Service").
          </p>
          <p style={paraStyle}>
            By creating an account, accessing, or using the Service, you agree to be bound by these Terms.
            If you are using the Service on behalf of a brokerage, team, or organization, you represent that
            you have authority to bind that entity to these Terms. If you do not agree to these Terms, do
            not use the Service.
          </p>
        </div>

        {/* 1. Account & Eligibility */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. Account Registration & Eligibility</h2>
          <p style={paraStyle}>
            To use {PRODUCT_NAME}, you must register for an account by providing accurate, complete, and
            current information. You must be at least 18 years of age and, if acting as a real estate
            professional, hold the requisite licenses in your state of practice.
          </p>
          <p style={paraStyle}>
            You are responsible for maintaining the confidentiality of your account credentials and for all
            activities that occur under your account. You agree to notify us immediately at {SUPPORT_EMAIL} if
            you suspect any unauthorized use of your account.
          </p>
          <p style={paraStyle}>
            {PRODUCT_NAME} accounts are provisioned on a per-seat basis by the subscribing brokerage or
            organization ("Subscriber"). The Subscriber's designated administrator controls seat allocation,
            user roles, and permissions within their organization.
          </p>
        </div>

        {/* 2. Subscription & Billing */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Subscription & Billing</h2>
          <div style={subHeadingStyle}>2.1 Pricing</div>
          <p style={paraStyle}>
            The Service is offered at a flat rate of $79 per seat per month, billed to the Subscriber.
            Pricing is uniform across all brokerages and team sizes. The Subscriber is responsible for
            payment on behalf of all users within their organization.
          </p>

          <div style={subHeadingStyle}>2.2 Included Allowances</div>
          <p style={paraStyle}>Each seat includes the following monthly allowances:</p>
          <ul style={listStyle}>
            <li>60 minutes of AI voice calling (via Vapi integration)</li>
            <li>10 e-signature envelopes (via BoldSign integration)</li>
            <li>SMS and WhatsApp messaging (fair-use, subject to carrier fees)</li>
          </ul>

          <div style={subHeadingStyle}>2.3 Booster Packs</div>
          <p style={paraStyle}>
            Additional capacity is available via on-demand Booster Packs at $29 each, which include
            60 additional voice minutes, 10 additional e-signature envelopes, and 100 additional SMS credits.
            Booster Packs are non-refundable and expire at the end of the billing cycle.
          </p>

          <div style={subHeadingStyle}>2.4 Billing Cycle & Payment</div>
          <p style={paraStyle}>
            Subscriptions are billed monthly in advance. All fees are non-refundable except as required
            by applicable law. We accept major credit cards and ACH transfers. Failure to pay may result
            in suspension or termination of access.
          </p>

          <div style={subHeadingStyle}>2.5 Price Changes</div>
          <p style={paraStyle}>
            We may adjust pricing with at least 30 days' prior written notice to the Subscriber's
            account email. Continued use of the Service after the effective date of a price change
            constitutes acceptance of the new pricing.
          </p>
        </div>

        {/* 3. Use of the Service */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. Permitted Use & Restrictions</h2>
          <div style={subHeadingStyle}>3.1 Permitted Use</div>
          <p style={paraStyle}>
            {PRODUCT_NAME} is designed for licensed real estate brokerages, agents, and their administrative
            teams. You may use the Service to manage contacts, transactions, documents, communications,
            marketing, and related real estate business operations.
          </p>

          <div style={subHeadingStyle}>3.2 Restrictions</div>
          <p style={paraStyle}>You agree not to:</p>
          <ul style={listStyle}>
            <li>Use the Service for any unlawful purpose or in violation of any applicable law or regulation, including but not limited to the New Jersey Real Estate Licensing Act</li>
            <li>Reverse engineer, decompile, disassemble, or attempt to derive source code from the Service</li>
            <li>Resell, sublicense, or redistribute the Service or any portion thereof without our prior written consent</li>
            <li>Use the Service to send unsolicited communications in violation of the Telephone Consumer Protection Act (TCPA), CAN-SPAM Act, or applicable state telemarketing laws</li>
            <li>Upload or transmit malicious code, viruses, or any harmful software</li>
            <li>Attempt to gain unauthorized access to other accounts, systems, or networks connected to the Service</li>
            <li>Use the AI features to generate content that is deceptive, defamatory, or designed to impersonate another person</li>
            <li>Exceed fair-use messaging thresholds in a manner that disrupts the Service for other users</li>
          </ul>
        </div>

        {/* 4. AI Features */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. AI-Powered Features</h2>
          <p style={paraStyle}>
            {PRODUCT_NAME} includes artificial intelligence features ("AI Brain") that provide conversational
            assistance, content generation, document drafting, lead scoring, and automated workflows. By
            using these features, you acknowledge and agree that:
          </p>
          <ul style={listStyle}>
            <li>AI-generated content is provided as a tool to assist your professional judgment and does not constitute legal, financial, or real estate advice</li>
            <li>You are solely responsible for reviewing, verifying, and approving any AI-generated content before use in professional communications, contracts, or filings</li>
            <li>AI outputs may contain errors, inaccuracies, or incomplete information, and {PRODUCT_NAME} makes no warranty as to the accuracy, completeness, or suitability of AI-generated content</li>
            <li>Your data may be processed by third-party AI model providers to deliver AI features, subject to our Privacy Policy and data processing agreements with such providers</li>
            <li>You will not use AI features to generate content that violates applicable fair housing laws, anti-discrimination regulations, or professional ethics rules</li>
          </ul>
        </div>

        {/* 5. Communications */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Communications & Messaging</h2>
          <p style={paraStyle}>
            {PRODUCT_NAME} enables outbound communications via SMS, WhatsApp, email, and AI voice calls.
            You are solely responsible for obtaining proper consent from recipients before initiating
            communications through the Service, in compliance with:
          </p>
          <ul style={listStyle}>
            <li>The Telephone Consumer Protection Act (TCPA)</li>
            <li>The CAN-SPAM Act</li>
            <li>State-specific telemarketing and do-not-call regulations</li>
            <li>WhatsApp Business Policy and Meta's platform terms</li>
            <li>Any applicable NJREC (New Jersey Real Estate Commission) regulations regarding solicitation</li>
          </ul>
          <p style={paraStyle}>
            {PRODUCT_NAME} provides tools (e.g., STIR/SHAKEN caller ID verification, opt-out management)
            to support compliance but does not guarantee compliance on your behalf. You agree to indemnify
            {PRODUCT_NAME} against any claims arising from your communications practices.
          </p>
        </div>

        {/* 6. E-Signatures */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Electronic Signatures</h2>
          <p style={paraStyle}>
            {PRODUCT_NAME} provides electronic signature functionality powered by BoldSign. Electronic
            signatures executed through the Service are intended to comply with the Electronic Signatures
            in Global and National Commerce (ESIGN) Act and the Uniform Electronic Transactions Act (UETA)
            as adopted in New Jersey.
          </p>
          <p style={paraStyle}>
            You acknowledge that: (a) you are responsible for ensuring all parties consent to electronic
            signature processes; (b) certain documents may require wet signatures under applicable law; and
            (c) {PRODUCT_NAME} does not provide legal advice regarding the enforceability of electronic
            signatures in your specific transactions.
          </p>
        </div>

        {/* 7. Your Data */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Your Data & Content</h2>
          <div style={subHeadingStyle}>7.1 Ownership</div>
          <p style={paraStyle}>
            You retain all ownership rights in the data, content, files, and materials you upload or
            create using the Service ("Your Data"). {PRODUCT_NAME} does not claim ownership of Your Data.
          </p>

          <div style={subHeadingStyle}>7.2 License Grant</div>
          <p style={paraStyle}>
            You grant {PRODUCT_NAME} a non-exclusive, worldwide, royalty-free license to host, store,
            process, and display Your Data solely for the purpose of providing and improving the Service.
            This license terminates when you delete Your Data or your account is terminated.
          </p>

          <div style={subHeadingStyle}>7.3 Data Portability</div>
          <p style={paraStyle}>
            You may export Your Data at any time using the export tools provided within the Service.
            Upon account termination, we will make Your Data available for export for a period of 30 days,
            after which it may be permanently deleted.
          </p>

          <div style={subHeadingStyle}>7.4 Uploaded Forms & Documents</div>
          <p style={paraStyle}>
            Certain features allow you to upload proprietary forms (e.g., NJAR-licensed real estate forms).
            You represent and warrant that you hold valid licenses for any copyrighted forms you upload,
            and {PRODUCT_NAME} shall not be liable for any intellectual property claims arising from forms
            you upload.
          </p>
        </div>

        {/* 8. Third-Party Integrations */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Third-Party Integrations</h2>
          <p style={paraStyle}>
            The Service integrates with third-party platforms including but not limited to Zillow (Tech Connect),
            Facebook Lead Ads, Google/Microsoft email services, Twilio (SMS/voice), Meta Cloud API (WhatsApp),
            BoldSign (e-signatures), and various lead sources. Your use of these integrations is subject to
            the respective third-party terms and policies.
          </p>
          <p style={paraStyle}>
            {PRODUCT_NAME} is not responsible for the availability, accuracy, or conduct of third-party
            services. We may add, modify, or discontinue integrations at any time with reasonable notice.
          </p>
        </div>

        {/* 9. Website Tracking (Trovva Pixel) */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Website Tracking (Trovva Pixel)</h2>
          <p style={paraStyle}>
            {PRODUCT_NAME} offers an optional website tracking feature ("Trovva Pixel") that allows you to
            monitor visitor activity on your brokerage website. If you install the Trovva Pixel, you are
            responsible for:
          </p>
          <ul style={listStyle}>
            <li>Disclosing the use of tracking technology in your website's privacy policy</li>
            <li>Obtaining any required consent from website visitors under applicable privacy laws</li>
            <li>Complying with all applicable state and federal privacy regulations, including the California Consumer Privacy Act (CCPA) where applicable</li>
          </ul>
        </div>

        {/* 10. Intellectual Property */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Intellectual Property</h2>
          <p style={paraStyle}>
            The Service, including its design, code, features, AI models, algorithms, branding, and
            documentation, is owned by {COMPANY_NAME} and protected by copyright, trademark, trade secret,
            and other intellectual property laws. These Terms do not grant you any rights to {PRODUCT_NAME}'s
            intellectual property except the limited right to use the Service as described herein.
          </p>
        </div>

        {/* 11. Disclaimers */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>
            <AlertCircle size={20} color={theme.colors.warning} />
            11. Disclaimers
          </h2>
          <p style={{ ...paraStyle, fontWeight: 500, textTransform: 'uppercase', fontSize: 13 }}>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER
            EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p style={paraStyle}>
            {PRODUCT_NAME} does not warrant that the Service will be uninterrupted, error-free, or secure,
            or that any defects will be corrected. We do not warrant the accuracy of AI-generated content,
            lead data from third-party sources, or market information displayed within the Service.
          </p>
        </div>

        {/* 12. Limitation of Liability */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>12. Limitation of Liability</h2>
          <p style={{ ...paraStyle, fontWeight: 500, textTransform: 'uppercase', fontSize: 13 }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY_NAME.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF
            PROFITS, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF
            THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY.
          </p>
          <p style={{ ...paraStyle, fontWeight: 500, textTransform: 'uppercase', fontSize: 13 }}>
            OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS RELATED TO THE SERVICE SHALL NOT EXCEED THE
            AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
          </p>
        </div>

        {/* 13. Indemnification */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>13. Indemnification</h2>
          <p style={paraStyle}>
            You agree to defend, indemnify, and hold harmless {COMPANY_NAME}, its officers, directors,
            employees, and agents from and against any claims, liabilities, damages, losses, and expenses
            (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Service;
            (b) your violation of these Terms; (c) your violation of any applicable law or third-party right;
            (d) any content you upload or transmit through the Service; or (e) any communications you send
            using the Service.
          </p>
        </div>

        {/* 14. Termination */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>14. Termination</h2>
          <p style={paraStyle}>
            Either party may terminate this agreement at any time. You may cancel your subscription through
            your account settings or by contacting {SUPPORT_EMAIL}. We may suspend or terminate your access
            if you breach these Terms, fail to pay fees, or engage in conduct that is harmful to other users
            or the Service.
          </p>
          <p style={paraStyle}>
            Upon termination: (a) your right to use the Service ceases immediately; (b) you will have 30 days
            to export Your Data; (c) any outstanding fees become immediately due; and (d) provisions that by
            their nature should survive termination (including Sections 7, 11-13, and 16) shall survive.
          </p>
        </div>

        {/* 15. Modifications to Terms */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>15. Modifications</h2>
          <p style={paraStyle}>
            We may update these Terms from time to time. We will notify you of material changes by email
            to your account address and/or by posting a notice within the Service at least 15 days before
            the changes take effect. Your continued use of the Service after the effective date of any
            modification constitutes acceptance of the updated Terms.
          </p>
        </div>

        {/* 16. Governing Law & Disputes */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>16. Governing Law & Dispute Resolution</h2>
          <p style={paraStyle}>
            These Terms are governed by and construed in accordance with the laws of the State of New Jersey,
            without regard to conflict of law principles. Any dispute arising under these Terms shall first
            be submitted to good-faith mediation. If mediation is unsuccessful, disputes shall be resolved
            by binding arbitration conducted in New Jersey under the rules of the American Arbitration
            Association, except that either party may seek injunctive relief in the state or federal courts
            located in New Jersey.
          </p>
          <p style={paraStyle}>
            You agree to bring any claims on an individual basis only, and waive any right to participate
            in a class action or class arbitration.
          </p>
        </div>

        {/* 17. Miscellaneous */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>17. General Provisions</h2>
          <p style={paraStyle}>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and
            {PRODUCT_NAME} regarding the Service. If any provision of these Terms is held to be invalid
            or unenforceable, the remaining provisions shall remain in full force and effect. Our failure
            to enforce any right or provision shall not be deemed a waiver. You may not assign or transfer
            these Terms without our prior written consent. We may assign these Terms in connection with a
            merger, acquisition, or sale of all or substantially all of our assets.
          </p>
        </div>

        {/* Contact */}
        <div style={{
          padding: 24, borderRadius: 12,
          background: `${theme.colors.primary}08`,
          border: `1px solid ${theme.colors.primary}20`,
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, marginBottom: 8, marginTop: 0 }}>
            Contact Us
          </h2>
          <p style={{ ...paraStyle, margin: 0 }}>
            If you have questions about these Terms of Service, please contact us at{' '}
            <a href={`mailto:${LEGAL_EMAIL}`} style={{ color: theme.colors.primary }}>{LEGAL_EMAIL}</a>
            {' '}or write to: {COMPANY_NAME}, New Jersey, United States.
          </p>
        </div>
      </div>
    </div>
  );
}
